// @flow

import { Component } from 'react';
import { cloneDeep, isEmpty } from 'lodash';
import { H1 } from '@blueprintjs/core';

import WidgetGrid from './widget-grid';
import ReportTabs from './report-tabs';
import Layout from '../../layouts/default-logged-in';
import EmailLayout from '../../layouts/email';
import ReportWithChat from './ReportWithChat';
import { ReportRenderProvider } from '../../../contexts/report-render-context';
import {
  Row,
  TopBar,
} from '../summary/layout';

import * as ReportModel from '../../../models/report';
import { trackEvent, ENTER_PAGE } from '../../../services/mixpanel';
import * as PERIOD_PRESETS from '../../../constants/period-presets';
import * as PREVIOUS_PERIOD_PRESETS from '../../../constants/previous-period-presets';
import { updateReportConfigByHierarchy } from '../../../utils/reports';
import * as ROUTES from '../../../constants/routes';
import { successToast, errorToast } from '../../../utils/toaster';

import * as PeriodModel from '../../../models/period';
import * as LocationModel from '../../../models/location';
import * as AvailableMetricsModel from '../../../models/available-metrics';
import type { BrowserHistory } from 'history/createBrowserHistory';
import { IS_EMAIL_VIEW } from '../../../config/vars';
import { Mobile } from '../../layouts/devices-sizes';
import styled from 'styled-components';

type State = {
  report: ReportModel.t,
  isScheduledReportJobExplorerOpen: boolean,
  perms: { canUpdate: boolean, canDelete: boolean, canRead?: boolean },
  showDeleteConfirm: boolean,
  showShareConfirm: boolean,
};

type Props = {
  history: BrowserHistory,
  allReports: ReportModel.t[],
  startingReport: ReportModel.t,
  allLocationsList: LocationModel.t[], // TODO: need to supply this, and use it in the locations selector
  fetchingQueryData: boolean, // TODO: need to supply this, and also fire an action every time a widget starts/ends a request
  availableMetrics: AvailableMetricsModel.t,
  showStaffFilters: boolean,
  startOfWeek: 'monday' | 'sunday',
};

const StyledH1 = styled(H1)`
  color: var(--report-heading-text);
  font-family: var(--fonts-heading);
  max-width: var(--widget-max-width);
  width: 100%;
  text-align: center;
  margin: 0.5rem auto 2rem;
`;

const LocationCountWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  max-width: var(--widget-max-width);
  margin: 1rem auto 2rem;
`;

const LocationCount = styled.div`
  color: rgba(0, 0, 0, 0.5);
  text-align: center;
  padding: 1rem;
  font-size: 14px;
  max-width: 30rem;
`;

class SingleReportPage extends Component<Props, State> {
  state = {
    report: {},
    isScheduledReportJobExplorerOpen: false,
    perms: { canUpdate: false, canDelete: false, canRead: false },
    showDeleteConfirm: false,
    showShareConfirm: false,
  };

  componentDidMount() {
    try {
      trackEvent(ENTER_PAGE, 'REPORTS');
      if (!this.props.startingReport) return false;
      this.setState({ report: cloneDeep(this.props.startingReport) });
    } catch (e) {
      console.error(e);
    }
  }

  componentDidUpdate() {
    try {
      const { startingReport = {} } = this.props;
      const { report = {} } = this.state;

      if (!startingReport || !report) return false;

      if (startingReport.id !== report.id) {
        this.setState({ report: cloneDeep(startingReport) });
      }
    } catch (e) {
      console.error(e);
    }
  }

  handleTopLevelExcludeStaff() {
    const excludeStaff =
      Selectors.isDemographicFilterRoleCustomer(this.state.report.config) ||
      !this.props.showStaffFilters;

    // toggle the demographicFilter - if already enabled revert to an empty object othweise add customer only filter
    const newValue = excludeStaff ? {} : { role: 'customer' };

    this.updateReportConfig({
      property: 'demographicFilter',
      newValue,
      hierarchyLevel: 0,
      hierarchyIndex: 0,
    });
  }

  updateReportConfig({
    property,
    newValue,
    hierarchyLevel,
    hierarchyIndex,
  }: {
    property: string,
    newValue: any,
    hierarchyLevel: number,
    hierarchyIndex: number,
  }) {
    if (isEmpty(this.state.report)) return;

    const report = updateReportConfigByHierarchy(
      this.state.report,
      property,
      newValue,
      hierarchyLevel,
      hierarchyIndex,
    );

    this.setState({ report });
  }

  openScheduledReportJobExplorer() {
    this.setState({ isScheduledReportJobExplorerOpen: true });
  }
  closeScheduledReportJobExplorer() {
    this.setState({ isScheduledReportJobExplorerOpen: false });
  }

  render() {
    const {
      allReports,
      history,
      allLocationsList,
      fetchingQueryData = false, // TODO: get this data somehow. Can't use normal way as API fetches aren't going through redux actions. Need to dispatch load events when doing API calls
      availableMetrics,
      showStaffFilters,
      startOfWeek,
    } = this.props;
    const {
      report = {},
      isScheduledReportJobExplorerOpen,
      perms,
      showDeleteConfirm,
      showShareConfirm,
    } = this.state;

    const { config: reportConfig = {} } = report || {};

    const getHierarchyConfig = (
      parent: Object = {},
      child: Object = {},
    ): Object => {
      return {
        locations: child.locations || parent.locations,
        period: child.period || parent.period,
        comparisonPeriod: child.comparisonPeriod || parent.comparisonPeriod,
        demographicFilter: child.demographicFilter || parent.demographicFilter,
      };
    };

    const transformHierarchyConfig = (config: Object): Object => {
      // get the dateoverride from the URL, if present
      const params = new URL(window.document.location).searchParams;
      const overrideDateString = params.get('overrideDate');
      let overrideDate;
      if (overrideDateString) {
        overrideDate = new Date(overrideDateString);
      }

      // set a hard default to stop flow complaining
      let period = PeriodModel.getExplorerPeriod(
        PERIOD_PRESETS.lastFullDay,
        overrideDate,
      );
      // set no default comp period
      let comparisonPeriod = {};

      if (config.period) {
        // if the period is custom then it should already have the dates
        if (
          config.period.selectedPreset === PERIOD_PRESETS.custom &&
          config.period.selectedDates
        ) {
          period = config.period;
        } else {
          period = PeriodModel.getExplorerPeriod(
            config.period.selectedPreset,
            overrideDate,
          );
        }
      }

      if (config.comparisonPeriod && period && period.selectedDates) {
        const selectedPreset = config.comparisonPeriod.selectedPreset;

        // Preserve custom comparison dates instead of recomputing (which can overwrite user-selected ranges)
        const isCustomCompare = selectedPreset === PREVIOUS_PERIOD_PRESETS.custom;
        if (isCustomCompare && config.comparisonPeriod.selectedDates) {
          comparisonPeriod = config.comparisonPeriod;
        } else if (selectedPreset) {
          comparisonPeriod = PeriodModel.getExplorerComparePeriod(
            selectedPreset,
            period.selectedDates,
          );
        }
      }

      return {
        locations:
          config.locations === 'all'
            ? allLocationsList.map((l) => l.id)
            : config.locations,
        period,
        comparisonPeriod,
        demographicFilter: config.demographicFilter,
      };
    };

    const hierarchyConfig = getHierarchyConfig(reportConfig);
    const topLevelConfig = transformHierarchyConfig(hierarchyConfig);

    // Build resolved config with all periods computed for markdown export
    const buildResolvedConfig = () => {
      const periodKey = (p) => {
        if (!p || !p.selectedDates) return '';
        return `${String(p.selectedPreset || '')}|${String(p.selectedDates.start || '')}|${String(p.selectedDates.end || '')}`;
      };

      const compareKey = (cp) => {
        if (!cp) return '';
        if (cp.active === false) return 'inactive';
        if (!cp.selectedDates) return String(cp.selectedPreset || '');
        return `${String(cp.selectedPreset || '')}|${String(cp.selectedDates.start || '')}|${String(cp.selectedDates.end || '')}|active`;
      };

      const resolvedSections = (reportConfig.sections || []).map((section) => {
        const sectionHierarchyConfig = getHierarchyConfig(reportConfig, section);
        const resolvedSectionConfig = transformHierarchyConfig(sectionHierarchyConfig);

        const resolvedWidgets = (section.widgets || []).map((widget) => {
          const widgetHierarchyConfig = getHierarchyConfig(
            resolvedSectionConfig,
            widget.widgetConfig
          );
          const resolvedWidgetConfig = transformHierarchyConfig(widgetHierarchyConfig);

          return {
            ...widget,
            widgetConfig: {
              ...widget.widgetConfig,
              ...resolvedWidgetConfig,
            },
          };
        });

        return {
          ...section,
          period: resolvedSectionConfig.period,
          comparisonPeriod: resolvedSectionConfig.comparisonPeriod,
          widgets: resolvedWidgets,
        };
      });

      // Option B: if report-level period/comparison aren't explicitly set, infer them from sections.
      // This prevents markdown showing a default report-level date range when the UI is section-driven.
      const hasExplicitReportPeriod = !!(reportConfig.period && reportConfig.period.selectedDates);
      const hasExplicitReportComparison = !!(
        reportConfig.comparisonPeriod && reportConfig.comparisonPeriod.selectedPreset
      );

      const firstSection = resolvedSections[0] || {};

      const inferredPeriod = (() => {
        if (hasExplicitReportPeriod) return topLevelConfig.period;
        const firstKey = periodKey(firstSection.period);
        if (!firstKey) return undefined;
        const allSame = resolvedSections.every((s) => periodKey(s.period) === firstKey);
        return allSame ? firstSection.period : undefined;
      })();

      const inferredComparisonPeriod = (() => {
        if (hasExplicitReportComparison) return topLevelConfig.comparisonPeriod;
        const firstKey = compareKey(firstSection.comparisonPeriod);
        if (!firstKey) return undefined;
        const allSame = resolvedSections.every(
          (s) => compareKey(s.comparisonPeriod) === firstKey
        );
        return allSame ? firstSection.comparisonPeriod : undefined;
      })();

      return {
        ...reportConfig,
        period: inferredPeriod,
        comparisonPeriod: inferredComparisonPeriod,
        sections: resolvedSections,
      };
    };

    const resolvedReportConfig = buildResolvedConfig();

    // For markdown generation, pass only the effective (selected) locations.
    // This allows markdown location metadata to reflect both saved config and user overrides.
    const effectiveLocationIds =
      (topLevelConfig && Array.isArray(topLevelConfig.locations) && topLevelConfig.locations) ||
      (allLocationsList ? allLocationsList.map((l) => l.id) : []);
    const effectiveLocationsForMarkdown =
      allLocationsList && effectiveLocationIds && effectiveLocationIds.length
        ? allLocationsList.filter((l) => effectiveLocationIds.includes(l.id))
        : allLocationsList;

    if (IS_EMAIL_VIEW)
      return (
        <EmailLayout>
          <WidgetGrid
            report={report}
            getHierarchyConfig={getHierarchyConfig}
            transformHierarchyConfig={transformHierarchyConfig}
            updateReportConfig={this.updateReportConfig.bind(this)}
            availableMetrics={availableMetrics}
            allLocationsList={allLocationsList}
            startOfWeek={startOfWeek}
          />
          {allLocationsList && topLevelConfig && topLevelConfig.locations && (
            <LocationCountWrapper>
              <LocationCount>
                This report covers {topLevelConfig.locations.length} of{' '}
                {allLocationsList.length} locations.
                <br />
                <br />
                {report.id}
              </LocationCount>
            </LocationCountWrapper>
          )}
        </EmailLayout>
      );

    const pathname =
      (history && history.location && history.location.pathname) ||
      window.location.pathname ||
      '';
    const isMyReports = pathname.startsWith(ROUTES.MY_REPORTS);
    const isOrgReports = pathname.startsWith(ROUTES.REPORTS) && !isMyReports;

    // My Reports: always allow delete and edit regardless of org-level permissions
    const canShowDelete = isMyReports || !!perms.canDelete;
    const canShowShare = isMyReports && !!perms.canUpdate;
    const canShowEdit = isMyReports || (isOrgReports && !!perms.canUpdate);

    const handleEdit = () => {
      const target = ROUTES.AI_REPORTS_EDIT.replace(':reportId', report.id);
      const fromPath =
        (history && history.location && history.location.pathname) || '';
      history.push({ pathname: target, state: { from: fromPath } });
    };

    const handleDelete = () => this.setState({ showDeleteConfirm: true });
    const handleShare = () => this.setState({ showShareConfirm: true });

    const confirmDelete = async () => {
      try {
        // Pre-compute next report target before deletion to avoid timing issues
        const orgReportsPostDelete = (allReports || [])
          .filter((r) => r && !r.ownerId && r.id !== report.id);
        const myReportsPostDelete = (allReports || [])
          .filter((r) => r && !!r.ownerId && r.id !== report.id);

        if (!this.props.deleteDashboardReport) {
          throw new Error('Delete function not available');
        }
        await this.props.deleteDashboardReport(report.id);
        this.setState({ showDeleteConfirm: false });
        successToast({ message: 'Report deleted.' });
        if (isOrgReports && orgReportsPostDelete && orgReportsPostDelete.length > 0) {
          history.push(ROUTES.SINGLE_REPORT.replace(':reportId', orgReportsPostDelete[0].id));
        } else if (isMyReports && myReportsPostDelete && myReportsPostDelete.length > 0) {
          history.push(ROUTES.SINGLE_MY_REPORT.replace(':reportId', myReportsPostDelete[0].id));
        } else {
          const base = isMyReports ? ROUTES.MY_REPORTS : ROUTES.REPORTS;
          history.push(base);
        }
      } catch (e) {
        this.setState({ showDeleteConfirm: false });
        errorToast({
          message: 'Unable to delete report. Please try again later.',
        });
      }
    };

    const confirmShare = async () => {
      try {
        if (!this.props.shareDashboardReport) {
          throw new Error('Share function not available');
        }
        await this.props.shareDashboardReport(report.id);
        this.setState({ showShareConfirm: false });
        successToast({ message: 'Report shared.' });
        history.push(ROUTES.SINGLE_REPORT.replace(':reportId', report.id));
      } catch (e) {
        this.setState({ showShareConfirm: false });
        errorToast({
          message: 'Unable to share report. Please try again later.',
        });
      }
    };

    return (
      <Layout
        history={history}
        isLoading={fetchingQueryData}
        location={history.location}
      >
          <Mobile>
            <TopBar>
              <Row>
                <ReportTabs allReports={allReports} history={history} />
              </Row>
            </TopBar>
          </Mobile>
          <ReportRenderProvider
            reportSpec={resolvedReportConfig}
            reportName={report.name}
            reportDescription={report.description}
            locations={effectiveLocationsForMarkdown}
            startOfWeek={startOfWeek}
          >
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
              <ReportWithChat
              report={report}
              reportConfig={reportConfig}
              resolvedReportConfig={resolvedReportConfig}
              topLevelConfig={topLevelConfig}
              allLocationsList={allLocationsList}
              availableMetrics={availableMetrics}
              startOfWeek={startOfWeek}
              showStaffFilters={showStaffFilters}
              fetchingQueryData={fetchingQueryData}
              history={history}
              updateReportConfig={(args) => this.updateReportConfig(args)}
              handleTopLevelExcludeStaff={() => this.handleTopLevelExcludeStaff()}
              openScheduledReportJobExplorer={() => this.openScheduledReportJobExplorer()}
              closeScheduledReportJobExplorer={() => this.closeScheduledReportJobExplorer()}
              canShowEdit={canShowEdit}
              canShowShare={canShowShare}
              canShowDelete={canShowDelete}
              onEdit={handleEdit}
              onShare={handleShare}
              onDelete={handleDelete}
              perms={perms}
              onPermissionsChange={(p) =>
                this.setState({ perms: { ...this.state.perms, ...p } })
              }
              showDeleteConfirm={showDeleteConfirm}
              showShareConfirm={showShareConfirm}
              confirmDelete={confirmDelete}
              confirmShare={confirmShare}
              setShowDeleteConfirm={(show) => this.setState({ showDeleteConfirm: show })}
              setShowShareConfirm={(show) => this.setState({ showShareConfirm: show })}
              allReports={allReports}
              isScheduledReportJobExplorerOpen={isScheduledReportJobExplorerOpen}
              getHierarchyConfig={getHierarchyConfig}
              transformHierarchyConfig={transformHierarchyConfig}
              isOrgReports={isOrgReports}
            />
            </div>
          </ReportRenderProvider>
        </Layout>
    );
  }
}

export default SingleReportPage;
